import { bottomChromeHeightAtom, FLOATING_CHROME_GAP, floatingActionSlotsAtom } from "@/atoms/bottom-chrome";
import { CHROME_Z_INDEX } from "@/contexts/ModalContext";
import { cn } from "@/lib/cn";
import { useAtomValue, useSetAtom } from "jotai";
import { useId, useLayoutEffect, useRef, type ReactNode } from "react";
import { APP_SHELL_WIDTH } from "./FixedFooter";

/** Props of `FloatingAction`; `offset` is extra px above the bottom chrome (e.g. a sibling floating button). */
export type FloatingActionProps = {
  side?: "left" | "right";
  offset?: number;
  className?: string;
  children: ReactNode;
};

function useFloatingFootprint(offset: number) {
  const id = useId();
  const setSlots = useSetAtom(floatingActionSlotsAtom);
  const nodeRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const node = nodeRef.current;
    if (!node) return;
    const publish = () => {
      const extent = offset + node.getBoundingClientRect().height;
      setSlots((slots) => (slots[id] === extent ? slots : { ...slots, [id]: extent }));
    };
    const observer = new ResizeObserver(publish);
    observer.observe(node);
    publish();
    return () => {
      observer.disconnect();
      setSlots((slots) => {
        if (!(id in slots)) return slots;
        const next = { ...slots };
        delete next[id];
        return next;
      });
    };
  }, [id, offset, setSlots]);

  return nodeRef;
}

/**
 * Pins round floating buttons to a corner of the storefront column, riding above whatever fixed footer is visible.
 * Publishes its footprint so `PageContainer bottom="chrome"` keeps the last row of content clear of it.
 */
export function FloatingAction({ side = "right", offset = 0, className, children }: FloatingActionProps) {
  const bottomChrome = useAtomValue(bottomChromeHeightAtom);
  const footprintRef = useFloatingFootprint(offset);

  return (
    <div
      style={{ zIndex: CHROME_Z_INDEX.bar, bottom: bottomChrome + offset + FLOATING_CHROME_GAP }}
      className="pointer-events-none fixed left-0 right-0 transition-[bottom] duration-slow ease-standard"
    >
      <div
        className={cn(
          "mx-auto flex px-4",
          APP_SHELL_WIDTH,
          side === "left" ? "justify-start" : "justify-end",
          className,
        )}
      >
        <div ref={footprintRef} className="pointer-events-auto flex">
          {children}
        </div>
      </div>
    </div>
  );
}
